import React, { useCallback, useEffect, useState } from 'react';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  strapiDownloadWorkOrderPdf,
  strapiOpenWorkOrderPdfHtmlPreview,
  strapiWorkOrderPdfStatus,
} from '../../api/strapiCrm';
import { strapiFetchMyUserSigned } from '../../api/strapiUserSigned';
import { canOpenWorkOrderPdfPreview, hasProfileSignature } from './workOrderPdfAccess';

type Props = {
  open: boolean;
  workOrderDocumentId: string | null;
  jobNumber?: string | null;
  companyName?: string | null;
  jwt: string | null;
  /** PIC marketing: tombol OK di warning mengarah ke My profile. */
  canSign: boolean;
  showHtmlPreview?: boolean;
  onClose: () => void;
};

type DialogPhase = 'checking' | 'blocked' | 'pdf';

const PIC_DEFAULT_SIGN_REQUIRED =
  'PIC marketing perlu menyimpan tanda tangan default di My profile sebelum PDF work order dapat dibuat.';

function sanitizeFilePart(value: string): string {
  return value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function workOrderPdfFileName(opts: {
  companyName?: string | null;
  jobNumber?: string | null;
  documentId?: string | null;
}): string {
  const company = sanitizeFilePart(opts.companyName ?? '');
  const number =
    sanitizeFilePart(opts.jobNumber ?? '') || sanitizeFilePart(opts.documentId ?? '') || 'work-order';
  const base = company ? `${company}_${number}` : number;
  return `${base}_WO.pdf`;
}

export function WorkOrderPdfPreviewDialog({
  open,
  workOrderDocumentId,
  jobNumber,
  companyName,
  jwt,
  canSign,
  showHtmlPreview = false,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<DialogPhase>('checking');
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [openingHtml, setOpeningHtml] = useState(false);

  const loadPdf = useCallback(
    async (opts?: { keepPreview?: boolean }) => {
      if (!workOrderDocumentId || !jwt) return;
      setLoading(true);
      setError(null);
      if (!opts?.keepPreview) {
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      }
      try {
        const blob = await strapiDownloadWorkOrderPdf(jwt, workOrderDocumentId, {
          cacheBust: Boolean(opts?.keepPreview),
        });
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Gagal memuat PDF work order.');
      } finally {
        setLoading(false);
      }
    },
    [jwt, workOrderDocumentId],
  );

  useEffect(() => {
    if (!open) {
      setPhase('checking');
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setError(null);
      setLoading(false);
      setRegenerating(false);
      setOpeningHtml(false);
      return;
    }
    if (!workOrderDocumentId || !jwt) {
      setPhase('blocked');
      setError('Sesi tidak valid. Masuk ulang, lalu coba lagi.');
      return;
    }
    let cancelled = false;
    setPhase('checking');
    setError(null);
    void (async () => {
      try {
        const [mine, signed] = await Promise.all([
          canSign ? strapiFetchMyUserSigned(jwt) : Promise.resolve(null),
          canSign ? Promise.resolve(false) : strapiWorkOrderPdfStatus(jwt, workOrderDocumentId),
        ]);
        if (cancelled) return;
        if (
          canOpenWorkOrderPdfPreview({
            canSign,
            hasProfileSignature: hasProfileSignature(mine),
            workOrderSigned: signed,
          })
        ) {
          setPhase('pdf');
          return;
        }
        setPhase('blocked');
        setError(PIC_DEFAULT_SIGN_REQUIRED);
      } catch (e) {
        if (cancelled) return;
        setPhase('blocked');
        setError(e instanceof Error ? e.message : 'Gagal memeriksa status PDF work order.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workOrderDocumentId, jwt, canSign]);

  useEffect(() => {
    if (!open || phase !== 'pdf' || !workOrderDocumentId || !jwt) return undefined;
    void loadPdf();
    return undefined;
  }, [open, phase, workOrderDocumentId, jwt, loadPdf]);

  async function handleRegenerate() {
    if (!workOrderDocumentId || !jwt || loading || regenerating) return;
    setRegenerating(true);
    try {
      await loadPdf({ keepPreview: true });
    } finally {
      setRegenerating(false);
    }
  }

  async function handleOpenHtmlPreview() {
    if (!workOrderDocumentId || !jwt || loading || regenerating || openingHtml) return;
    setOpeningHtml(true);
    setError(null);
    try {
      await strapiOpenWorkOrderPdfHtmlPreview(jwt, workOrderDocumentId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal membuka preview HTML work order.');
    } finally {
      setOpeningHtml(false);
    }
  }

  function handleDownload() {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = workOrderPdfFileName({
      companyName,
      jobNumber,
      documentId: workOrderDocumentId,
    });
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function handleBlockedOk() {
    onClose();
    if (canSign && (error == null || error === PIC_DEFAULT_SIGN_REQUIRED)) {
      navigate('/profile');
    }
  }

  const titleLabel = jobNumber?.trim() || workOrderDocumentId || 'Work order';
  const pdfBusy = loading || regenerating;
  const busy = pdfBusy || openingHtml;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      fullWidth
      maxWidth={phase === 'pdf' ? 'lg' : 'sm'}
      aria-labelledby="work-order-pdf-preview-title"
      data-testid="work-order-pdf-preview-dialog"
    >
      <DialogTitle id="work-order-pdf-preview-title" sx={{ pr: 6 }}>
        {`PDF Work order — ${titleLabel}`}
        <IconButton
          aria-label="Tutup"
          onClick={onClose}
          disabled={busy}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {phase === 'checking' ? (
        <DialogContent>
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4, gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Memeriksa tanda tangan default PIC…
            </Typography>
          </Stack>
        </DialogContent>
      ) : null}

      {phase === 'blocked' ? (
        <>
          <DialogContent>
            <Alert severity="warning" data-testid="work-order-pdf-blocked">
              {error || PIC_DEFAULT_SIGN_REQUIRED}
            </Alert>
          </DialogContent>
          <DialogActions>
            {canSign ? <Button onClick={onClose}>Tutup</Button> : null}
            <Button variant="contained" onClick={handleBlockedOk} data-testid="work-order-pdf-blocked-ok">
              OK
            </Button>
          </DialogActions>
        </>
      ) : null}

      {phase === 'pdf' ? (
        <>
          <DialogContent
            dividers
            sx={{
              p: 0,
              height: { xs: '70vh', md: '80vh' },
              bgcolor: 'grey.100',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {error ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
              </Box>
            ) : null}
            {!error && pdfBusy && !pdfUrl ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 2, p: 3 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  {regenerating ? 'Menggenerate ulang PDF work order…' : 'Menyiapkan PDF work order…'}
                </Typography>
              </Stack>
            ) : null}
            {!error && pdfUrl ? (
              <Box sx={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
                {pdfBusy ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 2,
                      bgcolor: 'rgba(255,255,255,0.72)',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary">
                      Menggenerate ulang PDF work order…
                    </Typography>
                  </Stack>
                ) : null}
                <Box
                  component="iframe"
                  src={pdfUrl}
                  title={`PDF Work order — ${titleLabel}`}
                  sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
                />
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
            <Button
              onClick={() => void handleRegenerate()}
              disabled={!workOrderDocumentId || !jwt || busy}
              startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <AutorenewOutlinedIcon />}
              data-testid="work-order-pdf-regenerate"
            >
              Generate ulang
            </Button>
            {showHtmlPreview ? (
              <Button
                onClick={() => void handleOpenHtmlPreview()}
                disabled={!workOrderDocumentId || !jwt || busy}
                startIcon={openingHtml ? <CircularProgress size={16} color="inherit" /> : <OpenInNewOutlinedIcon />}
                data-testid="work-order-pdf-html-preview"
              >
                Preview HTML
              </Button>
            ) : null}
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleDownload} disabled={!pdfUrl || busy} startIcon={<DownloadOutlinedIcon />}>
              Unduh PDF
            </Button>
            <Button onClick={onClose} disabled={busy}>
              Tutup
            </Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}
